// pages/history.js — the History page: which chats were most active over
// the last week to year, what they did, what the person asked them, and
// plain-language insights. Data: GET /v1/history?range= and, for an opened
// chat, GET /v1/history/chats/{id}?range=. On this computer only: the
// history is read from the chats' own logs here and never leaves it.
import { api, AuthRedirectError } from "../api.js";
import { agentsState } from "../page-state.js";
import { escapeHTML, formatUSD } from "../util.js";
import { chatLabel } from "../format.js";
import { navigate } from "../router.js";

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "1y", label: "1 year" },
];
const RANGE_KEY = "ac.historyRange";
const STACKED = 8;
const M = { l: 44, r: 12, t: 12, b: 26 };
const PAGE_ROWS = 25;

const hi = {
  range: readRange(),
  data: null,
  error: "",
  inflight: false,
  open: "",
  details: {},
  q: "",
  showAll: false,
  rows: PAGE_ROWS,
};

function readRange() {
  try {
    const v = localStorage.getItem(RANGE_KEY);
    if (RANGES.some((r) => r.key === v)) return v;
  } catch {
    // Storage blocked: the default range still works.
  }
  return "30d";
}

function saveRange(v) {
  try {
    localStorage.setItem(RANGE_KEY, v);
  } catch {
    // Storage blocked: the range just isn't remembered.
  }
}

const $ = (id) => document.getElementById(id);
const num = (n) => Number(n || 0).toLocaleString();
const baseName = (p) => (p ? String(p).replace(/\/+$/, "").split("/").pop() : "");

function chatName(c) {
  return c.name || c.repo || chatLabel(c.agent_id);
}

function chatSub(c) {
  return [c.repo, c.branch && "branch " + c.branch, c.runtime].filter(Boolean).join(" · ");
}

// shortPath shows a changed file from its repo down, else its last parts.
function shortPath(p, repo) {
  const s = String(p || "");
  if (repo) {
    const i = s.lastIndexOf("/" + repo + "/");
    if (i >= 0) return s.slice(i + repo.length + 2);
  }
  const parts = s.split("/").filter(Boolean);
  return parts.slice(-3).join("/");
}

function money(v) {
  if (!v) return "–";
  return v >= 100 ? "$" + Math.round(v).toLocaleString() : formatUSD(v);
}

function hoursText(h) {
  return h === 1 ? "1 hour" : num(h) + " hours";
}

function dayLabel(ts, opts) {
  return new Date(ts * 1000).toLocaleDateString(undefined, opts || { weekday: "short", month: "short", day: "numeric" });
}

function timeLabel(ts) {
  return new Date(ts * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function clock(h) {
  if (h === 0) return "12am";
  if (h < 12) return h + "am";
  if (h === 12) return "12pm";
  return h - 12 + "pm";
}

// ---- data ----

async function load() {
  if (hi.inflight) return;
  hi.inflight = true;
  const range = hi.range;
  setBusy(true);
  try {
    const d = await api("/v1/history?range=" + encodeURIComponent(range));
    if (range !== hi.range) return;
    hi.data = d;
    hi.error = "";
  } catch (err) {
    if (err instanceof AuthRedirectError) return;
    hi.error = (err && err.message) || String(err);
  } finally {
    hi.inflight = false;
    setBusy(false);
  }
  render();
}

async function loadDetail(agentID) {
  const key = hi.range + "|" + agentID;
  if (hi.details[key]) return;
  hi.details[key] = { loading: true };
  try {
    hi.details[key] = await api("/v1/history/chats/" + encodeURIComponent(agentID) + "?range=" + encodeURIComponent(hi.range));
  } catch (err) {
    hi.details[key] = { error: (err && err.message) || String(err) };
  }
  if (hi.open === agentID) render();
}

function setBusy(on) {
  const host = $("history-body");
  if (host) host.classList.toggle("hi-busy", on && !!hi.data);
}

// ---- pieces ----

function trend(now, before) {
  if (!before) return "";
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return '<span class="hi-trend">same as before</span>';
  const up = pct > 0;
  return '<span class="hi-trend ' + (up ? "up" : "down") + '">' + (up ? "▲ " : "▼ ") + Math.abs(pct) + "% vs previous</span>";
}

function tilesHTML(d) {
  const t = d.totals;
  const p = d.previous;
  const tile = (label, value, sub, tip) =>
    '<div class="hi-tile"' + (tip ? ' data-tooltip="' + escapeHTML(tip) + '"' : "") + ">" +
    '<div class="hi-tile-label">' + escapeHTML(label) + "</div>" +
    '<div class="hi-tile-value">' + value + "</div>" +
    '<div class="hi-tile-sub">' + sub + "</div></div>";
  return (
    '<div class="hi-tiles">' +
    tile("Actions", num(t.actions), trend(t.actions, p.actions) || escapeHTML(num(t.edits) + " edits"), "Every tool call a chat made: file edits, commands and file reads.") +
    tile("Chats", num(t.chats), trend(t.chats, p.chats) || "with any activity", "Chats that did anything in this period.") +
    tile("Hours with chats working", num(t.active_hours), trend(t.active_hours, p.active_hours) || "at least one chat busy", "Clock hours in which at least one chat did something.") +
    tile("AI spend", money(t.spend_usd), "estimate at API rates", "Token counts are exact. The dollar figure is an estimate at API rates; a subscription is billed flat.") +
    "</div>"
  );
}

function insightsHTML(d) {
  if (!d.insights.length) return "";
  return (
    '<section class="panel hi-panel"><header class="panel-header"><div class="panel-title">Insights</div></header>' +
    '<div class="hi-insights">' +
    d.insights
      .map(
        (i) =>
          '<div class="hi-insight' + (i.agent_id ? " hi-link" : "") + '"' + (i.agent_id ? ' data-hi-open="' + escapeHTML(i.agent_id) + '" tabindex="0" role="button"' : "") + ">" +
          '<div class="hi-insight-title">' + escapeHTML(i.title) + "</div>" +
          '<div class="hi-insight-detail">' + escapeHTML(i.detail) + "</div></div>",
      )
      .join("") +
    "</div></section>"
  );
}

// stack picks the busiest chats to colour in the daily chart; the rest are
// summed as "Other chats". Long ranges are shown per week.
// When the range reaches back before any history, the chart starts at the
// first day with activity (at least a week shown).
function stack(d) {
  const first = d.by_day.findIndex((v) => v > 0);
  const from = Math.max(0, Math.min(first < 0 ? 0 : first, d.days.length - 7));
  const weekly = d.days.length - from > 120;
  const buckets = [];
  for (let i = from; i < d.days.length; i++) {
    if (!weekly || (i - from) % 7 === 0) buckets.push({ ts: d.days[i], idx: [] });
    buckets[buckets.length - 1].idx.push(i);
  }
  const top = d.chats.slice(0, STACKED);
  const series = top.map((c, k) => ({ id: c.agent_id, name: chatName(c), color: "var(--al-c" + (k + 1) + ")", values: [] }));
  const other = { id: "", name: "Other chats", color: "var(--al-other)", values: [] };
  for (const b of buckets) {
    let total = 0;
    for (const i of b.idx) total += d.by_day[i] || 0;
    let used = 0;
    series.forEach((s, k) => {
      let v = 0;
      for (const i of b.idx) v += top[k].daily[i] || 0;
      s.values.push(v);
      used += v;
    });
    other.values.push(Math.max(0, total - used));
    b.total = total;
    b.chats = Math.max(...b.idx.map((i) => d.chats_by_day[i] || 0));
  }
  if (other.values.some((v) => v > 0)) series.push(other);
  return { weekly, buckets, series, trimmed: from > 0 };
}

function niceTop(max) {
  if (max <= 0) return { top: 1, step: 1 };
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * mag).find((s) => s >= raw);
  return { top: Math.ceil(max / step) * step, step };
}

function gridSVG(w, h, top, step) {
  const ph = h - M.t - M.b;
  let s = "";
  for (let v = 0; v <= top + 1e-9; v += step) {
    const y = M.t + ph - (v / top) * ph;
    s += '<line class="al-grid' + (v === 0 ? " al-base" : "") + '" x1="' + M.l + '" x2="' + (w - M.r) + '" y1="' + y.toFixed(1) + '" y2="' + y.toFixed(1) + '"/>';
    s += '<text class="al-ylabel" x="' + (M.l - 8) + '" y="' + (y + 3.5).toFixed(1) + '">' + Math.round(v).toLocaleString() + "</text>";
  }
  return s;
}

function barsSVG(values, w, h, colorsFor, labelFor, cls) {
  const n = values.length;
  const totals = values.map((v) => (Array.isArray(v) ? v.reduce((a, b) => a + b, 0) : v));
  const { top, step } = niceTop(Math.max(0, ...totals));
  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  const slot = pw / n;
  const bw = Math.max(1, Math.min(28, slot * 0.72));
  let s = gridSVG(w, h, top, step);
  values.forEach((v, i) => {
    const parts = Array.isArray(v) ? v : [v];
    let y = M.t + ph;
    const x = M.l + i * slot + (slot - bw) / 2;
    parts.forEach((p, k) => {
      if (!p) return;
      const bh = (p / top) * ph;
      y -= bh;
      // A 1px gap between stacked parts keeps neighbouring colours apart.
      s += '<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + bw.toFixed(1) + '" height="' + Math.max(0, bh - (k < parts.length - 1 ? 1 : 0)).toFixed(1) + '" rx="1.5" style="fill:' + colorsFor(k) + '"/>';
    });
  });
  let lastX = -Infinity;
  for (let i = 0; i < n; i++) {
    const label = labelFor(i);
    if (!label) continue;
    const x = M.l + (i + 0.5) * slot;
    if (x - lastX < 56) continue;
    lastX = x;
    s += '<text class="al-xlabel" x="' + x.toFixed(1) + '" y="' + (h - 8) + '">' + escapeHTML(label) + "</text>";
  }
  s += '<rect class="hi-hit" data-hi-chart="' + cls + '" x="' + M.l + '" y="' + M.t + '" width="' + pw + '" height="' + ph + '" data-n="' + n + '"/>';
  return '<svg class="al-svg hi-chart-svg" width="' + w + '" height="' + h + '" role="img">' + s + "</svg>";
}

function dailyHTML(d, w) {
  const st = stack(d);
  hi.stack = st;
  const values = st.buckets.map((_, i) => st.series.map((s) => s.values[i]));
  const every = st.buckets.length <= 10 ? 1 : Math.ceil(st.buckets.length / 8);
  const label = (i) =>
    i % every === 0 ? dayLabel(st.buckets[i].ts, st.buckets.length > 14 ? { month: "short", day: "numeric" } : { weekday: "short", day: "numeric" }) : "";
  const legend = st.series
    .map(
      (s) =>
        '<span class="hi-key-item' + (s.id ? " hi-link" : "") + '"' + (s.id ? ' data-hi-open="' + escapeHTML(s.id) + '"' : "") + '><span class="hi-key" style="background:' + s.color + '"></span>' +
        escapeHTML(s.name) + "</span>",
    )
    .join("");
  return (
    '<section class="panel hi-panel"><header class="panel-header"><div class="panel-title">Actions per ' + (st.weekly ? "week" : "day") + "</div></header>" +
    '<div class="hi-chart" data-hi-wrap="daily">' +
    barsSVG(values, w, 220, (k) => st.series[k].color, label, "daily") +
    "</div>" +
    '<div class="hi-legend">' + legend + "</div>" +
    (st.trimmed ? '<div class="hi-chart-note">History begins ' + escapeHTML(dayLabel(st.buckets[0].ts, { month: "long", day: "numeric" })) + ". It comes from your chats' logs, which Claude Code keeps for a set number of days, and from what AgentCollision records from now on (kept for a year).</div>" : "") +
    "</section>"
  );
}

function hoursHTML(d, w) {
  return (
    '<section class="panel hi-panel"><header class="panel-header"><div class="panel-title">When your chats work</div>' +
    '<div class="hi-panel-note">Actions by hour of the day, on this computer\'s clock</div></header>' +
    '<div class="hi-chart" data-hi-wrap="hours">' +
    barsSVG(d.by_hour_of_day, w, 160, () => "var(--al-c1)", (i) => (i % 6 === 0 ? clock(i) : ""), "hours") +
    "</div></section>"
  );
}

function sparkSVG(values) {
  const w = 96;
  const h = 22;
  const max = Math.max(1, ...values);
  const n = values.length;
  const pts = values.map((v, i) => (n === 1 ? w / 2 : (i / (n - 1)) * w).toFixed(1) + "," + (h - 2 - (v / max) * (h - 4)).toFixed(1));
  return '<svg class="hi-spark" width="' + w + '" height="' + h + '" aria-hidden="true"><polyline points="' + pts.join(" ") + '"/></svg>';
}

function splitHTML(c) {
  const parts = [
    ["edits", c.edits, "var(--al-c3)"],
    ["commands", c.commands, "var(--al-c1)"],
    ["reads", c.reads, "var(--al-c4)"],
    ["other", c.other, "var(--al-other)"],
  ];
  const tip = parts
    .filter((p) => p[1] > 0)
    .map((p) => num(p[1]) + " " + p[0])
    .join(" · ");
  return (
    '<div class="hi-split" data-tooltip="' + escapeHTML(tip) + '">' +
    parts.map((p) => (p[1] ? '<span style="flex:' + p[1] + ";background:" + p[2] + '"></span>' : "")).join("") +
    "</div>"
  );
}

function visibleChats(d) {
  const q = hi.q.trim().toLowerCase();
  if (!q) return d.chats;
  return d.chats.filter((c) => [chatName(c), c.repo, c.branch, c.runtime, c.agent_id].some((v) => String(v || "").toLowerCase().includes(q)));
}

// A short go-ahead ("ok", "continue") is kept but shown quietly, so the
// requests that say what the chat was for stand out.
function isNudge(text) {
  return String(text).trim().length <= 14 && !/\s\S+\s/.test(String(text).trim());
}

function detailHTML(c) {
  const det = hi.details[hi.range + "|" + c.agent_id];
  const facts =
    '<div class="hi-facts">' +
    [
      ["First active", timeLabel(c.first_at)],
      ["Last active", timeLabel(c.last_at)],
      ["Days active", num(c.active_days)],
      ["Hours active", num(c.active_hours)],
    ]
      .map(([k, v]) => "<div><span>" + escapeHTML(k) + "</span><strong>" + escapeHTML(v) + "</strong></div>")
      .join("") +
    '<button type="button" class="btn btn-sm" data-hi-session="' + escapeHTML(c.agent_id) + '">Open in Sessions</button>' +
    "</div>";
  if (!det || det.loading) return facts + '<div class="hi-detail-loading"><span class="skel-bar"></span><span class="skel-bar"></span><span class="skel-bar w60"></span></div>';
  if (det.error) return facts + '<div class="hi-empty">Could not load this chat: ' + escapeHTML(det.error) + "</div>";
  const reqs = det.requests || [];
  const shown = hi.showAll ? reqs : reqs.slice(0, 12);
  const asked = reqs.length
    ? '<ol class="hi-asked">' +
      shown.map((r) => "<li" + (isNudge(r.text) ? ' class="hi-nudge"' : "") + "><time>" + escapeHTML(timeLabel(r.at)) + "</time><p>" + escapeHTML(r.text) + "</p></li>").join("") +
      "</ol>" +
      (reqs.length > shown.length ? '<button type="button" class="btn btn-sm btn-ghost" data-hi-all="1">Show all ' + num(reqs.length) + " requests</button>" : "")
    : '<div class="hi-empty">No typed requests found for this chat. Only Claude Code keeps them in its logs.</div>';
  const files = det.files || [];
  const changed = files.length
    ? '<ul class="hi-files">' +
      files.slice(0, 12).map((f) => '<li data-tooltip="' + escapeHTML(f.path) + '"><span>' + escapeHTML(shortPath(f.path, c.repo)) + "</span><strong>" + num(f.edits) + "×</strong></li>").join("") +
      "</ul>" +
      (files.length > 12 ? '<div class="hi-more">+' + num(files.length - 12) + " more files</div>" : "")
    : '<div class="hi-empty">No file changes recorded.</div>';
  return (
    facts +
    '<div class="hi-detail-cols">' +
    '<div><h4>What you asked' + (reqs.length ? " <span>" + num(reqs.length) + "</span>" : "") + "</h4>" + asked + "</div>" +
    '<div><h4>Files it changed most' + (files.length ? " <span>" + num(files.length) + "</span>" : "") + "</h4>" + changed + "</div>" +
    "</div>"
  );
}

function tableHTML(d) {
  const all = visibleChats(d);
  const chats = all.slice(0, hi.rows);
  const rows = chats
    .map((c) => {
      const open = hi.open === c.agent_id;
      return (
        '<tr class="hi-row' + (open ? " open" : "") + '" data-hi-row="' + escapeHTML(c.agent_id) + '" tabindex="0" aria-expanded="' + open + '">' +
        '<td class="hi-rank">' + (d.chats.indexOf(c) + 1) + "</td>" +
        '<td class="hi-chat"><div class="hi-name">' + escapeHTML(chatName(c)) + "</div>" +
        '<div class="hi-sub">' + escapeHTML(chatSub(c)) + "</div></td>" +
        '<td class="hi-col-focus">' + (c.focus ? '<span class="hi-focus hi-focus-' + escapeHTML(c.focus.toLowerCase().replace(/\s+/g, "-")) + '">' + escapeHTML(c.focus) + "</span>" : "") + "</td>" +
        '<td class="hi-col-spark">' + sparkSVG(c.daily) + "</td>" +
        '<td class="hi-num"><strong>' + num(c.actions) + "</strong>" + splitHTML(c) + "</td>" +
        '<td class="hi-num hi-col-hours">' + num(c.active_hours) + "</td>" +
        '<td class="hi-num hi-col-req">' + (c.requests ? num(c.requests) : "–") + "</td>" +
        '<td class="hi-num hi-col-spend">' + money(c.spend_usd) + "</td>" +
        "</tr>" +
        (open ? '<tr class="hi-detail-row"><td colspan="8"><div class="hi-detail">' + detailHTML(c) + "</div></td></tr>" : "")
      );
    })
    .join("");
  return (
    '<section class="panel hi-panel"><header class="panel-header hi-table-head"><div class="panel-title">Most active chats <span class="count">' + num(d.totals.chats) + "</span></div>" +
    '<input type="search" class="hi-search" id="history-search" placeholder="Find a chat, repo or branch" aria-label="Find a chat" value="' + escapeHTML(hi.q) + '"></header>' +
    '<div class="hi-table-wrap"><table class="hi-table"><thead><tr>' +
    '<th>#</th><th>Chat</th><th class="hi-col-focus" data-tooltip="What this chat did more of than your other chats: editing files, running commands or reading">Mostly</th>' +
    '<th class="hi-col-spark">Activity</th><th class="hi-num">Actions</th><th class="hi-num hi-col-hours" data-tooltip="Clock hours in which the chat did something">Hours</th>' +
    '<th class="hi-num hi-col-req" data-tooltip="Messages you typed to the chat">Requests</th><th class="hi-num hi-col-spend" data-tooltip="Estimate at API rates">Spend</th>' +
    "</tr></thead><tbody>" +
    (rows || '<tr><td colspan="8" class="hi-empty">No chats match “' + escapeHTML(hi.q) + "”.</td></tr>") +
    "</tbody></table></div>" +
    (all.length > chats.length || d.chats.length < d.totals.chats
      ? '<div class="hi-table-more">' +
        (all.length > chats.length ? '<button type="button" class="btn btn-sm" data-hi-more="1">Show ' + num(Math.min(PAGE_ROWS, all.length - chats.length)) + " more</button>" : "") +
        "<span>" + num(chats.length) + " of " + num(all.length) + (d.chats.length < d.totals.chats ? " most active (of " + num(d.totals.chats) + " chats)" : "") + "</span></div>"
      : "") +
    "</section>"
  );
}

function sharedHTML(d) {
  if (!d.shared_files.length) return "";
  const byID = new Map(d.chats.map((c) => [c.agent_id, c]));
  return (
    '<section class="panel hi-panel"><header class="panel-header"><div class="panel-title">Files several chats changed</div>' +
    '<div class="hi-panel-note">Where chats are most likely to collide</div></header>' +
    '<ul class="hi-shared">' +
    d.shared_files
      .map((f) => {
        const names = f.chats.map((id) => (byID.has(id) ? chatName(byID.get(id)) : chatLabel(id)));
        return (
          '<li data-tooltip="' + escapeHTML(f.path) + '"><span class="hi-shared-path">' + escapeHTML(shortPath(f.path, "")) + "</span>" +
          '<span class="hi-shared-who">' + escapeHTML(names.slice(0, 4).join(", ") + (names.length > 4 ? " +" + (names.length - 4) : "")) + "</span>" +
          "<strong>" + num(f.chats.length) + " chats · " + num(f.edits) + " edits</strong></li>"
        );
      })
      .join("") +
    "</ul></section>"
  );
}

function skeletonHTML() {
  const tile = '<div class="pg-skel-tile"><span class="skel-bar w40"></span><span class="skel-bar big w60"></span><span class="skel-bar w80"></span></div>';
  const bars = Array.from({ length: 14 }, (_, i) => {
    const h = [40, 62, 35, 80, 55, 70, 45, 90, 60, 50, 75, 38, 66, 52][i];
    return '<rect class="hi-skel-bar" style="animation-delay:' + i * 60 + 'ms" x="' + (10 + i * 42) + '" y="' + (120 - h) + '" width="28" height="' + h + '" rx="2"/>';
  }).join("");
  return (
    '<div class="pg-skel" role="status" aria-label="Loading history">' +
    '<div class="pg-skel-tiles">' + tile + tile + tile + tile + "</div>" +
    '<div class="pg-skel-chart"><svg viewBox="0 0 600 124" preserveAspectRatio="none" aria-hidden="true">' + bars + "</svg></div>" +
    '<div class="pg-skel-rows"><span class="skel-bar"></span><span class="skel-bar"></span><span class="skel-bar"></span><span class="skel-bar"></span></div></div>'
  );
}

function rangeHTML() {
  return RANGES.map(
    (r) => '<button type="button" class="segmented-button' + (r.key === hi.range ? " active" : "") + '" data-hi-range="' + r.key + '" aria-pressed="' + (r.key === hi.range) + '">' + r.label + "</button>",
  ).join("");
}

// ---- render ----

function render() {
  const body = $("history-body");
  const ranges = $("history-range");
  if (!body) return;
  if (ranges) ranges.innerHTML = rangeHTML();
  const d = hi.data;
  if (!d) {
    body.innerHTML = hi.error
      ? '<section class="panel hi-panel"><div class="hi-empty"><strong>Could not load history.</strong> ' + escapeHTML(hi.error) +
        ' <button type="button" class="btn btn-sm" data-hi-retry="1">Try again</button></div></section>'
      : skeletonHTML();
    return;
  }
  if (!d.chats.length) {
    body.innerHTML =
      '<section class="panel hi-panel"><div class="hi-empty"><strong>No chat activity in the last ' + escapeHTML(RANGES.find((r) => r.key === d.range).label) + ".</strong>" +
      " History fills in from your chats' logs on this computer and from what AgentCollision sees from now on.</div></section>";
    return;
  }
  const search = document.activeElement && document.activeElement.id === "history-search";
  const w = Math.max(320, (body.clientWidth || 900) - 34);
  body.innerHTML = tilesHTML(d) + insightsHTML(d) + dailyHTML(d, w) + tableHTML(d) + '<div class="hi-two">' + hoursHTML(d, w) + sharedHTML(d) + "</div>";
  // Two panels side by side on wide screens: redraw the hour chart at its
  // column's width.
  const hourWrap = body.querySelector('[data-hi-wrap="hours"]');
  if (hourWrap && hourWrap.clientWidth && Math.abs(hourWrap.clientWidth - w) > 8) {
    hourWrap.innerHTML = barsSVG(d.by_hour_of_day, hourWrap.clientWidth, 160, () => "var(--al-c1)", (i) => (i % 6 === 0 ? clock(i) : ""), "hours");
  }
  if (search) {
    const input = $("history-search");
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

// ---- hover ----

function tip() {
  let el = document.getElementById("hi-tip");
  if (!el) {
    el = document.createElement("div");
    el.id = "hi-tip";
    el.className = "al-tip";
    el.hidden = true;
    document.body.appendChild(el);
  }
  return el;
}

function tipHTML(kind, i) {
  const d = hi.data;
  if (kind === "hours") {
    const v = d.by_hour_of_day[i];
    return '<div class="al-tip-head">' + clock(i) + "–" + clock((i + 1) % 24) + "</div><div><strong>" + num(v) + "</strong> actions in this period</div>";
  }
  const st = hi.stack;
  const b = st.buckets[i];
  const head = st.weekly ? "Week of " + dayLabel(b.ts, { month: "short", day: "numeric" }) : dayLabel(b.ts, { weekday: "long", month: "short", day: "numeric" });
  const rows = st.series
    .map((s) => ({ s, v: s.values[i] }))
    .filter((r) => r.v > 0)
    .map((r) => '<div class="al-tip-row"><span class="al-tip-key" style="background:' + r.s.color + '"></span><strong>' + num(r.v) + "</strong><span>" + escapeHTML(r.s.name) + "</span></div>")
    .join("");
  return (
    '<div class="al-tip-head">' + escapeHTML(head) + "</div>" +
    "<div><strong>" + num(b.total) + "</strong> actions" + (b.chats ? " · " + num(b.chats) + (b.chats === 1 ? " chat" : " chats") : "") + "</div>" +
    rows
  );
}

function onMove(ev) {
  const hit = ev.target.closest && ev.target.closest(".hi-hit");
  const el = tip();
  if (!hit || !hi.data) {
    el.hidden = true;
    return;
  }
  const r = hit.getBoundingClientRect();
  const n = Number(hit.dataset.n);
  const i = Math.min(n - 1, Math.max(0, Math.floor(((ev.clientX - r.left) / r.width) * n)));
  el.innerHTML = tipHTML(hit.dataset.hiChart, i);
  el.hidden = false;
  let left = ev.clientX + 14;
  if (left + el.offsetWidth > window.innerWidth - 8) left = ev.clientX - 14 - el.offsetWidth;
  let top = ev.clientY + 14;
  if (top + el.offsetHeight > window.innerHeight - 8) top = ev.clientY - 14 - el.offsetHeight;
  el.style.left = Math.max(8, left) + "px";
  el.style.top = Math.max(8, top) + "px";
}

// ---- events ----

function toggle(agentID) {
  hi.showAll = false;
  hi.open = hi.open === agentID ? "" : agentID;
  if (hi.open) loadDetail(hi.open);
  render();
  if (hi.open) {
    const row = document.querySelector('[data-hi-row="' + CSS.escape(hi.open) + '"]');
    if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }
}

function openFromElsewhere(agentID) {
  if (hi.q && !visibleChats(hi.data).some((c) => c.agent_id === agentID)) hi.q = "";
  const at = visibleChats(hi.data).findIndex((c) => c.agent_id === agentID);
  if (at >= hi.rows) hi.rows = at + 1;
  if (hi.open !== agentID) toggle(agentID);
}

function bind() {
  const page = document.querySelector('section.page[data-page="history"]');
  if (!page || page.dataset.hiBound) return;
  page.dataset.hiBound = "1";
  page.addEventListener("click", (e) => {
    const t = e.target;
    const range = t.closest("[data-hi-range]");
    if (range) {
      if (range.dataset.hiRange !== hi.range) {
        hi.range = range.dataset.hiRange;
        saveRange(hi.range);
        hi.open = "";
        hi.rows = PAGE_ROWS;
        load();
        render();
      }
      return;
    }
    if (t.closest("[data-hi-retry]")) return load();
    if (t.closest("[data-hi-more]")) {
      hi.rows += PAGE_ROWS;
      return render();
    }
    if (t.closest("[data-hi-all]")) {
      hi.showAll = true;
      return render();
    }
    const session = t.closest("[data-hi-session]");
    if (session) {
      agentsState.filter = session.dataset.hiSession;
      agentsState.showEnded = true;
      navigate("agents");
      const input = document.getElementById("agents-filter");
      if (input) input.value = session.dataset.hiSession;
      return;
    }
    const open = t.closest("[data-hi-open]");
    if (open) return openFromElsewhere(open.dataset.hiOpen);
    if (t.closest(".hi-detail")) return;
    const row = t.closest("[data-hi-row]");
    if (row) toggle(row.dataset.hiRow);
  });
  page.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const el = e.target.closest("[data-hi-row], [data-hi-open]");
    if (!el || e.target.tagName === "INPUT") return;
    e.preventDefault();
    if (el.dataset.hiRow) toggle(el.dataset.hiRow);
    else openFromElsewhere(el.dataset.hiOpen);
  });
  page.addEventListener("input", (e) => {
    if (e.target.id !== "history-search") return;
    hi.q = e.target.value;
    hi.rows = PAGE_ROWS;
    render();
  });
  page.addEventListener("mousemove", onMove);
  page.addEventListener("mouseleave", () => (tip().hidden = true));
  let resizeTimer = 0;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => page.classList.contains("active") && render(), 150);
  });
}

// ---- mount ----

const REFRESH_MS = 5 * 60 * 1000;
let timer = 0;

function start() {
  bind();
  render();
  load();
  clearInterval(timer);
  timer = setInterval(load, REFRESH_MS);
}

function stop() {
  clearInterval(timer);
  timer = 0;
  tip().hidden = true;
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="history"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  new MutationObserver(() => (section.classList.contains("active") ? timer || start() : stop())).observe(section, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bindLifecycle);
  else bindLifecycle();
}

export const _test = { hi, stack, tilesHTML, insightsHTML, tableHTML, sharedHTML, detailHTML, shortPath, money, chatName, niceTop };
